import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Checkbox,
  FormControlLabel,
  Typography,
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  IconButton,
  Chip,
  AvatarGroup,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import UpdateBrandData from "../UpdateBrandData/UpdateBrandData";
import DataStatusAlert from "../../../AssetManagement/Health/DataAccuracy/UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";
import {
  setModalOpen,
  setPage,
  setRowsPerPage,
  setSelectedRows,
  setSort,
} from "../../../../features/dataCollectionBrandsSlice";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import store from "../../../../store";
import { selectUserData } from "../../../../features/userSlice";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
// import { useDispatch } from "react-redux";

interface Props {
  tableHeadings: { [key: string]: string };
}

const BrandsTable = ({ tableHeadings }: Props) => {
  const {
    selectedDataCollectionBrands,
    page,
    sort,
    rowsPerPage,
    selectedAccount,
    selectedCompany,
    selectedWebsites,
    selectedStatus,
    selectedRawTableId,
    selectedInterface,
    selectedBrands,
    dataCollectionBrandsList,
    selectedRows,
    isModalOpen,
  } = useSelector((state: any) => state.dataCollectionBrands);
  const dispatch = useDispatch();
  const response = useSelector(selectUserData);
  const hasUpdateDataCollectionBrandsPermission = CheckPermissions(
    response?.data?.products.data_collection,
    "brands",
    "update_brand_data"
  );

  const [sortedData, setSortedData] = React.useState<any[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const headCells = Object.entries(tableHeadings);
  const [selectedRawTableIdSet, setSelectedRawTableIdSet] = useState<
    Set<string>
  >(new Set());
  useEffect(() => {
    if (selectedRawTableId && selectedRawTableId.length !== 0) {
      setSelectedRawTableIdSet(new Set(selectedRawTableId.map(String)));
    } else {
      setSelectedRawTableIdSet(new Set());
    }
  }, [selectedRawTableId]);

  const filteredData = sortedData?.filter((row: any) => {
    const selectedDataCollectionBrandSearch =
      selectedDataCollectionBrands && selectedDataCollectionBrands.length !== 0
        ? selectedDataCollectionBrands.includes(row.brand_name)
        : true;
    const selectedInterfaceFilter =
      selectedInterface && selectedInterface.length !== 0
        ? selectedInterface.some(
            (selectedInterface: any) =>
              selectedInterface.includes(`(${row.interface_id})`) // Check if the selected brand includes the brand_id
          )
        : true;
    const selectedBrandsFilter =
      selectedBrands && selectedBrands.length !== 0
        ? selectedBrands.some(
            (selectedBrand: any) => selectedBrand.includes(`(${row.brand_id})`) // Check if the selected brand includes the brand_id
          )
        : true;
    const selectedAccountFilter =
      selectedAccount && selectedAccount.length !== 0
        ? selectedAccount.includes(row.username)
        : true;
    const selectedCompanyFilter =
      selectedCompany && selectedCompany.length !== 0
        ? selectedCompany.includes(row.company_name)
        : true;
    const selectedWebsitesFilter =
      selectedWebsites && selectedWebsites.length !== 0
        ? selectedWebsites.includes(row.site_name)
        : true;
    const selectedStatusFilter =
      selectedStatus && selectedStatus.length !== 0
        ? selectedStatus.includes(String(row.brand_isactive))
        : true;
    const selectedRawTableIdFilter =
      selectedRawTableId && selectedRawTableId.length !== 0
        ? Array.isArray(row.raw_output_table_name) &&
          row.raw_output_table_name.some((item: any) =>
            selectedRawTableIdSet.has(String(item.table_id))
          )
        : true;

    return (
      selectedDataCollectionBrandSearch &&
      selectedAccountFilter &&
      selectedCompanyFilter &&
      selectedWebsitesFilter &&
      selectedStatusFilter &&
      selectedRawTableIdFilter &&
      selectedInterfaceFilter &&
      selectedBrandsFilter
    );
  });

  const tableheaders = [
    { id: 0, KEY: "brand_name", label: "Brand Name", sortable: true },
    { id: 1, KEY: "brand_id", label: "ID", sortable: true },
    { id: 2, KEY: "username", label: "Account", sortable: true },
    { id: 3, KEY: "company_name", label: "Company", sortable: true },
    { id: 4, KEY: "site_name", label: "Websites", sortable: true },
    { id: 5, KEY: "brand_isactive", label: "Status", sortable: true },
    {
      id: 6,
      KEY: "raw_output_table_name",
      label: "Raw Table ID",
      sortable: true,
    },
    { id: 7, KEY: "interface_name", label: "Interface", sortable: true },
    { id: 8, KEY: "quick_actions", label: "", sortable: false },
  ];

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable
    dispatch(
      setSort({
        keytoSort: header.KEY,
        // Toggle sort direction if the same column is clicked, otherwise default to ascending
        direction:
          header.KEY === sort.keytoSort && sort.direction === "asc"
            ? "desc"
            : "asc",
      })
    );
  };

  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        const aValue = a[keytoSort];
        const bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        // Custom logic for raw_output_table_name (array comparison)
        if (keytoSort === "raw_output_table_name") {
          const aCount = Array.isArray(aValue) ? aValue.length : 0;
          const bCount = Array.isArray(bValue) ? bValue.length : 0;

          if (sort.direction === "desc") {
            if (aCount === 0 && bCount > 0) return -1;
            if (bCount === 0 && aCount > 0) return 1;
            return bCount - aCount; // Descending by length
          } else {
            if (aCount === 0 && bCount > 0) return 1;
            if (bCount === 0 && aCount > 0) return -1;
            return aCount - bCount; // Ascending by length
          }
        }

        // Default sorting
        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort]
  );

  const sortedRows = getSortedData(filteredData || []);
  const paginatedFinalData = sortedRows?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const handleChangePage = (_: unknown, newPage: number) => {
    dispatch(setPage(newPage));
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("page", newPage.toString());
      return updatedParams;
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setRowsPerPage(+event.target.value));
    dispatch(setPage(0));
  };

  const handleClickOpen = (
    brandName: string,
    brandId: string | number,
    row_output_table_data: any
  ) => {
    dispatch(
      setModalOpen({
        isOpen: true,
        data: {
          brandName: brandName,
          brandId: brandId,
          row_output_table_data: row_output_table_data,
        },
      })
    );
  };

  useEffect(() => {
    dispatch(setSelectedRows([]));
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    const sortedRows = getSortedData(dataCollectionBrandsList || []);
    setSortedData(sortedRows);
    //eslint-disable-next-line
  }, [dataCollectionBrandsList, getSortedData]);

  useEffect(() => {
    if (page * rowsPerPage >= filteredData.length && page !== 0) {
      dispatch(setPage(0));
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set("page", "0");
      setSearchParams(updatedParams);
    }
    // eslint-disable-next-line
  }, [
    filteredData.length,
    rowsPerPage,
    selectedDataCollectionBrands,
    selectedAccount,
    selectedCompany,
    selectedWebsites,
    selectedStatus,
    selectedRawTableId,
    selectedInterface,
    selectedBrands,
  ]);

  useEffect(() => {
    const pageFromUrl = Number(searchParams.get("page")); // Convert to number
    store.dispatch(setPage(isNaN(pageFromUrl) ? 0 : pageFromUrl)); // Ensure it defaults correctly
  }, [searchParams]);

  const handleCheckboxChange = (rowId: string) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(rowId)) {
      newSelectedRows.delete(rowId); // Uncheck the checkbox
    } else {
      newSelectedRows.add(rowId); // Check the checkbox
    }
    dispatch(setSelectedRows(Array.from(newSelectedRows))); // Update the Redux store
  };

  return (
    <>
      <TableContainer className="BrandsTable">
        <Table aria-label="collapsible table" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableheaders.map((header: any) => (
                <TableCell key={header.id} align="left">
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {paginatedFinalData && paginatedFinalData.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={headCells.length}
                align="center"
                sx={{ padding: "20px", color: "#888", fontSize: "18px" }}
              >
                No Data To Display
              </TableCell>
            </TableRow>
          ) : (
            <TableBody>
              {paginatedFinalData.length > 0 &&
                paginatedFinalData.map((row, rowIndex) => (
                  <React.Fragment key={rowIndex}>
                    <TableRow
                      sx={{
                        "& > *": {
                          borderBottom:
                            "1px solid rgba(224, 224, 224, 1) !important",
                        },
                      }}
                    >
                      {headCells.map(([key], cellIndex) => (
                        <TableCell key={cellIndex} align="left">
                          {key === "raw_output_table_name" ? (
                            <div className="chip-data">
                              <Chip
                                label={
                                  Array.isArray(row[key]) && row[key].length > 0
                                    ? `${row[key][0]?.table_id}`
                                    : "N/A"
                                }
                                sx={{
                                  backgroundColor: "#7B1FA2",
                                  color: "#ffffff",
                                  height: "26px",
                                }}
                              />
                              <AvatarGroup
                                total={
                                  Array.isArray(row[key])
                                    ? row[key].length - 1
                                    : 0
                                }
                                variant="circular"
                              />
                            </div>
                          ) : key === "brand_name" ? (
                            <>
                              {" "}
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    color="secondary"
                                    checked={selectedRows.includes(
                                      row.brand_id
                                    )} // Check if the row is selected
                                    onChange={() =>
                                      handleCheckboxChange(row.brand_id)
                                    }
                                  />
                                }
                                label={
                                  <Link
                                    to="#"
                                    data-tracker="362"
                                    style={{
                                      textDecoration: "underline",
                                      color: "#9C27B0",
                                    }}
                                  >
                                    <Typography
                                      color="secondary"
                                      fontSize="14px"
                                    >
                                      {row[key] ? row[key] : "N/A"}
                                    </Typography>
                                  </Link>
                                }
                              />
                            </>
                          ) : key === "brand_id" ? (
                            <> {row[key] ? row[key] : "N/A"}</>
                          ) : key === "site_name" ? (
                            <div className="chip-data">
                              <Chip
                                label={
                                  row[key]
                                    ? Array.isArray(row[key])
                                      ? row[key][0]
                                      : row[key]
                                    : "N/A"
                                }
                                sx={{
                                  backgroundColor: "#7B1FA2",
                                  color: "#ffffff",
                                  height: "26px",
                                }}
                              />
                              <AvatarGroup
                                total={
                                  Array.isArray(row[key])
                                    ? row[key].length - 1
                                    : row[key]
                                    ? 0
                                    : 0
                                }
                                variant="circular"
                              />
                            </div>
                          ) : key === "username" ? (
                            <>
                              <Link
                                to="#"
                                style={{
                                  textDecoration: "underline",
                                  color: "#9C27B0",
                                }}
                              >
                                <Typography color="secondary" fontSize="14px">
                                  {row[key] ? row[key] : "N/A"}
                                </Typography>
                              </Link>
                            </>
                          ) : key === "company_name" ? (
                            <>{row[key] ? row[key] : "N/A"}</>
                          ) : key === "interface_name" ? (
                            <>{row[key] ? row[key] : "N/A"}</>
                          ) : key === "quick_actions" ? (
                            <>
                              <Stack direction="row">
                                <IconButton
                                  sx={{
                                    padding: "8px 8px 8px 0",
                                    color:
                                      !hasUpdateDataCollectionBrandsPermission ||
                                      !Array.isArray(
                                        row.raw_output_table_name
                                      ) ||
                                      row.raw_output_table_name.length === 0
                                        ? "rgba(0, 0, 0, 0.26)" // Custom disabled color (same as MUI default)
                                        : "secondary.main",
                                  }}
                                  disableRipple
                                  disabled={
                                    !hasUpdateDataCollectionBrandsPermission ||
                                    !Array.isArray(row.raw_output_table_name) ||
                                    row.raw_output_table_name.length === 0
                                  }
                                  onClick={() =>
                                    handleClickOpen(
                                      row.brand_name,
                                      row.brand_id,
                                      row.raw_output_table_name
                                    )
                                  }
                                >
                                  <PlayCircleFilledWhiteOutlinedIcon data-tracker="363" />
                                </IconButton>
                                {/* <IconButton disableRipple disabled>
                                  <EditOutlinedIcon
                                    data-tracker="364"
                                    sx={{
                                      color: (theme) =>
                                        theme.palette.action.disabled,
                                    }}
                                  />
                                </IconButton>
                                <IconButton disableRipple disabled>
                                  <DeleteOutlineOutlinedIcon
                                    data-tracker="365"
                                    sx={{
                                      color: (theme) =>
                                        theme.palette.action.disabled,
                                    }}
                                  />
                                </IconButton> */}
                              </Stack>
                            </>
                          ) : key === "brand_isactive" ? (
                            <DataStatusAlert
                              status={row[key] === 1 ? "Active" : "Inactive"}
                            />
                          ) : row[key] ? (
                            row[key]
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </React.Fragment>
                ))}
            </TableBody>
          )}
        </Table>
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={sortedRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
              },
            }}
          />
          <Pagination
            count={Math.ceil(filteredData?.length / rowsPerPage)}
            onChange={(_, value) => handleChangePage(null, value - 1)}
            page={page + 1}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={sortedRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginRight: "15px",
              },
            }}
          />
        </Stack>
        <UpdateBrandData open={isModalOpen} />
      </TableContainer>
    </>
  );
};

export default BrandsTable;
